import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";

import { useQuery } from "hooks";
import { dataURLtoFile, generateRandomString } from "utils/helper";
import * as services from "../services/policyApi";
import {
  setCurrentPolicy,
  setIsLoading,
  setPolicyCategory,
} from "../slices/policySlice";
import { useTranslation } from "react-i18next";

function toInitialValues(initialValues = {}) {
  if (!initialValues?.policyCategoryId) {
    return {};
  }
  // const policy = (initialValues.policy || [])[0] || {};
  const { attachment = [] } = initialValues?.attachment;
  const modifiedValues = {
    attachment: (attachment || []).map((a) => ({
      ...a,
      content: dataURLtoFile(`data:${a.ext};base64, ${a.content}`, a.fileName),
    })),
    policyCode: initialValues.policyCode,
    policyId: initialValues.policyId,
    policyName: initialValues.policyName,
    acknowledge: initialValues.acknowledge === "Y",
    announcementStatus: initialValues.announcementStatus === "Y",
    policyCategoryId: initialValues.policyCategoryId,
    link: initialValues?.link,
    description: initialValues?.description,
    detailType: initialValues?.detailType
  };
  return modifiedValues;
}

function toParams(params) {
  const modifiedParams = {
    ...params,
    category: params.policyCategoryId,
    acknowledge: params.acknowledge ? "Y" : "N",
  };
  return modifiedParams;
}

function usePolicy() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const query = useQuery();

  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.policy.isLoading);
  const currentPolicy = useSelector((state) => state.policy.currentPolicy);
  const policyCategory = useSelector((state) => state.policy.policyCategory);

  const initialValues = useMemo(() => {
    return toInitialValues(currentPolicy);
  }, [currentPolicy]);

  const modifiedPolicyCategory = useMemo(() => {
    const modifiedOptions = policyCategory.map((opt) => ({
      ...opt,
      label: opt.policyCategoryName,
      value: opt.policyCategoryId,
    }));
    return modifiedOptions;
  }, [policyCategory]);

  const getPolicyCategory = useCallback(async (values) => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getPolicyCategory({
        requestId: generateRandomString(),
        companyId: currentCompany,
        page: 0,
        size: 50,
        ...values,
      });
      const category = response?.data?.content[0]?.policyCategory || [];
      let bufferCategory = [...category];

      let idx = 0;
      for (let bufferCat of bufferCategory) {
        try {
          const res = await services.getPolicyByCategory({
            requestId: generateRandomString(),
            companyId: currentCompany,
            policyCategoryId: bufferCat.policyCategoryId,
            ...values
          });
          bufferCategory[idx] = {
            ...bufferCategory[idx],
            policyList: res.data?.policyList || [],
          };
        } catch (error) {
          message.error(t("common.error"))
        } finally {
          idx++;
        }
      }
      dispatch(setPolicyCategory(bufferCategory));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const getAnnouncementPolicy = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          policyCategoryId: values.policyCategoryId,
          policyAnnouncementId: values.policyAnnouncementId,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await services.getAnnouncementPolicy(modifiedValues);
        dispatch(setCurrentPolicy(response.data));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const createAnnouncementPolicy = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          ...values,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await services.createAnnouncementPolicy(
          toParams(modifiedValues)
        );
        message.success(t("common.success"))
        navigate("/news_policy/policy");
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, navigate]
  );

  const updateAnnouncementPolicy = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          ...values,
          policyAnnouncementId: values.policyId,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await services.updateAnnouncementPolicy(
          toParams(modifiedValues)
        );
        message.success(t("common.success"))
        navigate("/news_policy/policy");
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, navigate]
  );

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {i18n.language == "en"
                ? `Are you sure confirm to ${values.policyId ? "edit" : "create"} Policy ?`
                : `คุณแน่ใจหรือไม่ที่จะยืนยันการ ${values.policyId ? "แก้ไข" : "สร้าง"} นโยบาย ?`}
            </p>
          </>
        ),
        onOk() {
          if (values.policyId) {
            updateAnnouncementPolicy(values);
          } else {
            createAnnouncementPolicy(values);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [updateAnnouncementPolicy, createAnnouncementPolicy]
  );

  const onCancel = useCallback(() => {
    navigate("/news_policy/policy");
  }, [navigate]);

  useEffect(() => {
    const policyCategoryId = query.get("policyCategoryId");
    if (typeof currentCompany !== "object" && params.id && policyCategoryId) {
      getAnnouncementPolicy({
        policyCategoryId,
        policyAnnouncementId: params.id,
      });
    }
  }, [currentCompany, query, params.id, getAnnouncementPolicy]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getPolicyCategory({ page: 0, size: 50 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  return {
    isLoading,
    initialValues,
    policyCategory: modifiedPolicyCategory,
    onSubmit,
    onCancel,
  };
}

export default usePolicy;
